/**
 * @file Armazena constantes globais utilizadas em toda a aplicação.
 * Manter essas constantes centralizadas facilita a manutenção e garante consistência.
 */

/**
 * Versão atual da aplicação. Usada para controlar a exibição do modal de novidades.
 * @type {string}
 */
export const APP_VERSION = '7.1.3';

/**
 * Conteúdo em Markdown para o modal "O que há de novo".
 * @type {string}
 */
export const WHATS_NEW_CONTENT = `
## FELIZ DIA DAS MULHERES 💃🥂
### 🚀 **Confira os lançamentos:**
- **Trocas e Notificações:** As guias receberam um completo redesign para melhor usabilidade.
- **Projetos:** O novo submenu ao clicar com o botão direito no mapa permite visualizar o local no Street View, Abrir no Maps etc.
- **Dados do Ozmap:** Padronização do carregamento de dados conforme diretivas do Ozmap para melhor desempenho.
- **Cobertura:** Nova guia em fase beta permite identificar a CTO mais próxima de uma localidade, os postes, a distância e o local no Street View, facilitando a conferência.

Domingo, 08 de março de 2026.
`;

/**
 * Identificador único para a aplicação, usado para organizar os dados no Firestore.
 * @type {string}
 */
export const appId = 'dashboard-ftth';

/**
 * Array com os dados iniciais para as cidades monitoradas no dashboard.
 * Se nenhuma configuração for encontrada no banco de dados, estes valores serão usados como padrão.
 * @type {Array<Object>}
 */
export const initialCities = [
    { id: "Botucatu", nome: "Botucatu", total: 0, regularizados: 0 },
    { id: "LencoisPaulista", nome: "Lençóis Paulista", total: 0, regularizados: 0 },
    { id: "SaoManuel", nome: "São Manuel", total: 0, regularizados: 0 },
    { id: "Areiopolis", nome: "Areiópolis", total: 0, regularizados: 0 },
    { id: "Pratania", nome: "Pratânia", total: 0, regularizados: 0 },
];

/**
 * Array de objetos que define os possíveis status de um projeto.
 * Cada objeto contém o valor para o banco de dados, o rótulo para exibição,
 * a cor para gráficos e as classes de CSS para estilização de tags.
 * A ordem aqui influencia a ordem de exibição em legendas e filtros.
 * @type {Array<Object>}
 */
export const PROJECT_STATUSES = [
    { value: 'Comercial', label: 'Comercial', color: '#f97316', tagClasses: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' },
    { value: 'Viabilidade', label: 'Viabilidade', color: '#84cc16', tagClasses: 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-300' },
    { value: 'Em análise', label: 'Análise Técnica', color: '#facc15', tagClasses: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' },
    { value: 'Análise Aprovada', label: 'Análise Técnica Aprovada', color: '#a855f7', tagClasses: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' },
    { value: 'Em vistoria', label: 'Em vistoria', color: '#22d3ee', tagClasses: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300' },
    { value: 'Aprovado', label: 'Vistoria Aprovada', color: '#22c55e', tagClasses: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'},
    { value: 'Reprovado', label: 'Reprovado', color: '#ef4444', tagClasses: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' },
];

// =============================================
// --- CONSTANTES PARA A FUNCIONALIDADE DE TROCAS ---
// =============================================

/**
 * @description Define os possíveis status para uma troca de poste.
 * Usado para filtros, legendas de gráficos e badges de status.
 * - label: O texto que será exibido na interface para o usuário.
 * - value: O identificador único usado na lógica e no banco de dados.
 * - color: Uma cor associada ao status para uso em elementos visuais (ex: gráficos, badges).
 */
export const TRADE_STATUSES = [
    { value: 'hoje', label: 'Hoje', color: '#ef4444' }, // Vermelho para urgência
    { value: 'amanha', label: 'Amanhã', color: '#ffa500' }, // Vermelho para urgência
    { value: 'proximos', label: 'Depois de amanhã', color: '#f97316' }, // Laranja para atenção
    { value: 'cancelada', label: 'Canceladas', color: '#6b7280' }, // Cinza para inativo
    { value: 'sem_rede', label: 'Sem Rede', color: '#3b82f6' }, // Azul para informativo
];

/**
 * @description Mapeamento de status de troca por valor para acesso rápido.
 * Facilita a busca de um label ou cor de um status a partir de seu valor.
 * Ex: TRADE_STATUS_MAP['hoje'] retornará { label: 'Para Hoje', color: '#ef4444' }
 */
export const TRADE_STATUS_MAP = TRADE_STATUSES.reduce((acc, status) => {
    acc[status.value] = { label: status.label, color: status.color };
    return acc;
}, {});

// =============================================
// --- CONSTANTES PARA A FUNCIONALIDADE DE NOTIFICAÇÕES ---
// =============================================
export const STATUS = {
    PENDENTE: 'pendente',
    EM_EXECUCAO: 'em_execucao',
    FINALIZADA: 'finalizada',
    A_RESPONDER: 'a_responder',
};

// =============================================
// --- CONSTANTES PARA SERVIÇOS EXTERNOS ---
// =============================================
